import type { ComponentProps } from "react";

import type { DefenseFieldLabel } from "@/domain/defensePresentation";
import { cn } from "@/shared/lib/cn";

interface FieldDiagramProps extends Omit<ComponentProps<"svg">, "children"> {
  labels: readonly DefenseFieldLabel[];
  tone?: "screen" | "print";
  title: string;
}

const BASES = [
  [200, 320],
  [270, 250],
  [200, 180],
  [130, 250],
] as const;

export function FieldDiagram({
  className,
  labels,
  tone = "screen",
  title,
  ...props
}: FieldDiagramProps) {
  const screen = tone === "screen";

  return (
    <svg
      viewBox="0 0 400 370"
      role="img"
      aria-label={title}
      className={cn("block w-full", className)}
      {...props}
    >
      <path
        d="M 200 330 L 30 160 A 240 240 0 0 1 370 160 Z"
        fill={screen ? "var(--surface)" : "#d8ecd8"}
        stroke={screen ? "var(--border)" : "#8fbc8f"}
        strokeWidth="2"
      />
      <path
        d="M 200 330 L 285 245 A 120 120 0 0 0 115 245 Z"
        fill={screen ? "rgb(255 189 105 / 20%)" : "#e8d5b7"}
        stroke={screen ? "rgb(255 189 105 / 55%)" : "#c8a878"}
        strokeWidth="2"
      />
      <path
        d="M 200 320 L 270 250 L 200 180 L 130 250 Z"
        fill="none"
        stroke={screen ? "var(--foreground)" : "#ffffff"}
        strokeWidth="3"
      />
      {BASES.map(([x, y], index) => (
        <rect
          key={index}
          x={x - 6}
          y={y - 6}
          width="12"
          height="12"
          fill={screen ? "var(--foreground)" : "#ffffff"}
          stroke={screen ? "var(--border)" : "#999999"}
          transform={`rotate(45 ${x} ${y})`}
        />
      ))}
      <rect
        x="192"
        y="252"
        width="16"
        height="5"
        fill={screen ? "var(--foreground)" : "#ffffff"}
        stroke={screen ? "var(--border)" : "#999999"}
      />
      {labels.map(({ position, x, y, playerName, isOop }) => (
        <g key={position} textAnchor="middle">
          <title>
            {position}: {playerName}
            {isOop ? " — out of position" : ""}
          </title>
          <text
            x={x}
            y={y - 8}
            fontSize="10"
            fontFamily="var(--font-score)"
            fontWeight="700"
            fill={screen ? "var(--foreground-muted)" : "#666666"}
          >
            {position}
          </text>
          <rect
            x={x - 42}
            y={y - 4}
            width="84"
            height="18"
            rx="9"
            fill={
              screen
                ? isOop
                  ? "var(--danger)"
                  : "var(--warning)"
                : isOop
                  ? "#fff3cd"
                  : "#002b5c"
            }
            stroke={isOop && !screen ? "#d4a017" : "none"}
          />
          <text
            x={x}
            y={y + 9}
            fontSize="12"
            fontWeight="700"
            fill={
              screen
                ? "var(--warning-foreground)"
                : isOop
                  ? "#7a5c00"
                  : "#ffffff"
            }
            textLength={playerName.length > 11 ? 72 : undefined}
            lengthAdjust="spacingAndGlyphs"
          >
            {playerName}
            {isOop ? " ⚠" : ""}
          </text>
        </g>
      ))}
    </svg>
  );
}
